import { FallbackCard } from '@components/atoms/FallbackCard/FallbackCard';
import { Genre } from '@components/atoms/Genre/Genre';
import { Skeleton } from '@components/atoms/Skeleton/Skeleton';
import { CardTextSkeleton } from '@components/molecules/CardTextSkeleton/CardTextSkeleton';
import { GetChannelShowtimeNowDocument, type TvChannel, type TvChannelShowtime } from '@lib/graphql/generated';
import { useQuery } from '@lib/graphql/urql';
import { createDate } from '@src/utils/dateHelpers';
import { buildProgrammLink } from '@src/utils/programmLink';
import { getChannelLogo } from '@src/utils/sortedChannels';
import { Image, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { isTomorrow } from 'date-fns';
import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { FavoriteButton } from '../FavoriteButton/FavoriteButton';
import { GrowingElement, growingElementProgressClassProps } from '../GrowingElement/GrowingElement';

const ShowtimeProgress = dynamic(() =>
  import('@components/molecules/ShowtimeProgress/ShowtimeProgress').then((mod) => mod.ShowtimeProgress),
);

export interface TvCurrentProgramProps extends PropsWithClassProps {
  channel?: TvChannel & { showtimeData: TvChannelShowtime };
  channelEntry?: TvChannelShowtime;
  primetime?: boolean;
  showInitially?: boolean;
  showDate?: boolean;
  lastScroll?: number;
}

export const TvCurrentProgram = ({
  channel,
  channelEntry,
  showInitially,
  classProps,
  lastScroll,
  primetime = false,
  showDate,
}: TvCurrentProgramProps) => {
  const [inView, setInView] = useState(false);

  const pause = !!channel?.showtimeData?.id || !inView || !!channelEntry;
  const [showtimeNow, setShowtimeNow] = useState(undefined);

  const [{ fetching: showtimeLoading, data: channelData, stale }, refetch] = useQuery({
    query: GetChannelShowtimeNowDocument,
    variables: { id: channel?.id, primetime },
    pause,
    requestPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const showtime = channel?.showtimeData || channelData?.channelShowtimeNow || channelEntry;
    if (!showtimeLoading && channelData) {
      setShowtimeNow(channel?.showtimeData || channelData?.channelShowtimeNow);
    } else if (channelEntry) {
      const startTime = showtimeNow?.start?.slice(11, 16);
      const endTime = showtimeNow?.stop?.slice(11, 16);
      setShowtimeNow({ ...showtime, start_time: startTime, stop_time: endTime });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, channelData, showtimeLoading, channelEntry, showtimeNow?.start, showtimeNow?.stop]);

  // undefined until the first showtime arrives; null once loaded without one.
  const isLoading = showtimeNow === undefined;

  const channelLogo = useMemo(() => {
    return getChannelLogo(channel?.id || channelEntry?.channel?.id);
  }, [channel?.id, channelEntry?.channel?.id]);

  return (
    <GrowingElement
      showInitially={showInitially}
      classProps={classProps}
      lastScroll={lastScroll}
      key={showtimeNow?.id}
      onInViewChange={(inView) => setInView(inView)}
      link={buildProgrammLink(showtimeNow?.event_id, showtimeNow?.channel?.slug, showtimeNow?.title)}
      topSlot={
        <>
          {showtimeNow?.image?.url ? (
            <Image
              src={showtimeNow?.image.url}
              alt={showtimeNow?.title}
              width={350}
              height={200}
              imageMode="cover"
              copyright=""
              classProps={{
                container: 'absolute inset-0 rounded-t-lg overflow-hidden',
                root: 'w-full',
              }}
            />
          ) : isLoading ? (
            <Skeleton classProps={{ root: 'size-full rounded-b-none rounded-t-lg' }} />
          ) : (
            <FallbackCard classProps={{ root: 'overflow-hidden rounded-t-lg' }} />
          )}

          {channelLogo && (
            <div className="absolute top-2 right-3 h-5 w-8 rounded bg-surface/50 p-1">
              <div className="relative size-full">
                <Image
                  src={channelLogo}
                  alt={channel?.name || channelEntry?.channel?.name}
                  priority={true}
                  placeholder="empty"
                  width={48}
                  height={24}
                  copyright=""
                  classProps={{ root: 'h-auto max-h-full w-12 object-fit', container: 'flex items-center' }}
                />
              </div>
            </div>
          )}
        </>
      }
      topOverlaySlot={
        <ShowtimeProgress
          showtime={showtimeNow}
          onShowEnded={() => {
            if (!showtimeLoading && !pause && !stale && !primetime) {
              refetch();
            }
          }}
          progressClassProps={growingElementProgressClassProps}
        />
      }
      bottomSlot={
        isLoading ? (
          <CardTextSkeleton withAction />
        ) : (
          !!showtimeNow && (
            <>
              <div className="flex w-full justify-between gap-3 p-2 transition-spacings duration-150 canhover:group-hover:px-4 canhover:group-hover:pt-3">
                <div className="mt-1">
                  {showtimeNow?.start && showtimeNow?.stop && (
                    <div className="text-foreground/50 text-sm">
                      {showDate &&
                        `${
                          isToday(createDate(showtimeNow?.start))
                            ? 'Heute'
                            : isTomorrow(createDate(showtimeNow?.start))
                              ? 'Morgen'
                              : format(createDate(showtimeNow?.start), 'dd.MM')
                        }, `}
                      {format(createDate(showtimeNow?.start), 'HH:mm')}&nbsp;-&nbsp;
                      {format(createDate(showtimeNow?.stop), 'HH:mm')} Uhr
                    </div>
                  )}
                  <div
                    className={twMerge(
                      'hyphenate line-clamp-2 font-herokid font-medium text-gray-900 text-sm canhover:group-hover:line-clamp-1 canhover:group-hover:text-foreground md:text-base dark:text-foreground',
                    )}
                  >
                    {showtimeNow?.title}
                  </div>
                </div>
                <FavoriteButton id={showtimeNow?.event_id} type="TVChannelShowtime" classProps={{ root: 'mt-2' }} />
              </div>
              <div className="mt-2 grid h-0 w-full grid-rows-[1fr_auto] overflow-hidden px-4 canhover:sm:group-hover:h-28 canhover:sm:group-hover:pb-4">
                <div className="mt-1 flex flex-wrap content-start items-start gap-1">
                  {showtimeNow.genre && <Genre genre={{ title: showtimeNow.genre, id: '01' }} />}
                </div>
                <div className="flex items-center gap-2 pb-2 text-foreground text-sm">
                  {!!showtimeNow.year && <span className="font-bold">{showtimeNow.year}</span>}
                </div>
              </div>
            </>
          )
        )
      }
    />
  );
};
